import { useState, type FC, type FormEvent } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CreditCard, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '../../utils/cn';

interface AddPaymentMethodFormProps {
  onSuccess: (stripePaymentMethodId: string) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1f2937',
      fontFamily: 'Inter, system-ui, sans-serif',
      '::placeholder': {
        color: '#9ca3af',
      },
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626',
    },
  },
  hidePostalCode: false,
};

export const AddPaymentMethodForm: FC<AddPaymentMethodFormProps> = ({
  onSuccess,
  onCancel,
  isSubmitting = false,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!stripe || !elements) {
      setError('Payment system is not ready. Please try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card input not found. Please refresh and try again.');
      return;
    }

    setIsProcessing(true);

    try {
      const { paymentMethod, error: stripeError } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (stripeError) {
        setError(stripeError.message || 'Failed to process card. Please try again.');
        return;
      }

      if (!paymentMethod) {
        setError('Failed to create payment method. Please try again.');
        return;
      }

      await onSuccess(paymentMethod.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred.');
    } finally {
      setIsProcessing(false);
    }
  };

  const isLoading = isProcessing || isSubmitting;
  const canSubmit = stripe && elements && cardComplete && !isLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <CreditCard className="w-5 h-5 text-gray-600" />
        <h3 className="text-lg font-medium text-gray-900">Add Payment Method</h3>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div
        className={cn(
          'p-4 border rounded-lg transition-colors',
          error ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
        )}
      >
        <CardElement
          options={cardElementOptions}
          onChange={(e) => {
            setCardComplete(e.complete);
            if (e.error) {
              setError(e.error.message);
            } else {
              setError(null);
            }
          }}
        />
      </div>

      <p className="text-xs text-gray-500">
        Your card information is securely processed by Stripe. We do not store your full card
        details.
      </p>

      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className={cn(
            'flex-1 px-4 py-2.5 text-sm font-medium text-gray-700',
            'bg-white border border-gray-300 rounded-lg',
            'hover:bg-gray-50 transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className={cn(
            'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium',
            'text-white bg-primary-600 rounded-lg',
            'hover:bg-primary-700 transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Card'
          )}
        </button>
      </div>
    </form>
  );
};
